import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ChevronLeft, Radio, Calendar, Clock, Bell, BellRing, Check, X } from 'lucide-react';
import VideoModal from '../components/VideoModal';
import { Toaster, toast } from '../components/ui/sonner';
import { liveNow, upcoming, replays } from '../mock';

const NETFLIX_LOGO = 'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg';
const STORAGE_KEY = 'fat_reminders';
// Delay (ms) after which a reminded game "goes live" (simulated for demo)
const GO_LIVE_DELAY = 12000;

function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 bg-[#e50914] text-white text-xs font-bold px-2 py-0.5 rounded">
      <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> LIVE
    </span>
  );
}

export default function Sports() {
  const navigate = useNavigate();
  const [modal, setModal] = useState({ open: false, key: null, title: '' });
  const [reminders, setReminders] = useState([]); // ids of upcoming events
  const [liveIds, setLiveIds] = useState([]); // upcoming events that went live
  const [notifOpen, setNotifOpen] = useState(false);
  const timers = useRef({});
  const featured = liveNow[0];

  const play = useCallback(
    (ev) => setModal({ open: true, key: ev.youtube, title: ev.title }),
    []
  );

  // Load persisted reminders on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      setReminders(saved);
      saved.forEach((id) => scheduleGoLive(id));
    } catch (e) {
      /* ignore */
    }
    return () => Object.values(timers.current).forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (list) => {
    setReminders(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  };

  // Simulate a game going live after a delay -> fire an on-site reminder
  const scheduleGoLive = useCallback(
    (id) => {
      if (timers.current[id]) return;
      const ev = upcoming.find((u) => u.id === id);
      if (!ev) return;
      timers.current[id] = setTimeout(() => {
        setLiveIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
        toast(`${ev.title} is now LIVE`, {
          description: `${ev.sport} · Kick-off just started`,
          icon: <BellRing size={18} className="text-[#e50914]" />,
          duration: 10000,
          action: {
            label: 'Watch Now',
            onClick: () => play(ev),
          },
        });
        delete timers.current[id];
      }, GO_LIVE_DELAY);
    },
    [play]
  );

  const toggleReminder = (ev, e) => {
    e && e.stopPropagation();
    const has = reminders.includes(ev.id);
    if (has) {
      persist(reminders.filter((id) => id !== ev.id));
      if (timers.current[ev.id]) {
        clearTimeout(timers.current[ev.id]);
        delete timers.current[ev.id];
      }
      toast(`Reminder removed`, { description: ev.title });
    } else {
      persist([...reminders, ev.id]);
      scheduleGoLive(ev.id);
      toast(`We'll remind you`, {
        description: `${ev.title} · ${ev.time}`,
        icon: <Bell size={18} className="text-[#e50914]" />,
      });
    }
  };

  const remindedEvents = upcoming.filter((u) => reminders.includes(u.id));

  return (
    <div className="min-h-screen bg-[#141414] pb-16">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-b from-black/90 to-transparent">
        <div className="flex items-center justify-between px-4 md:px-12 py-3">
          <div className="flex items-center gap-6">
            <img src={NETFLIX_LOGO} alt="Netflix" className="h-5 md:h-7" />
            <span className="hidden sm:flex items-center gap-1.5 text-[#e50914] font-bold tracking-wide text-sm md:text-base">
              <Radio size={18} /> SPORTS LIVE
            </span>
          </div>
          <div className="flex items-center gap-4 md:gap-6">
            {/* Notification bell */}
            <div className="relative">
              <button
                onClick={() => setNotifOpen((o) => !o)}
                className="relative text-white hover:text-white/80 transition-colors p-1"
                aria-label="Reminders"
              >
                <Bell size={20} />
                {reminders.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#e50914] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                    {reminders.length}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-3 w-72 md:w-80 bg-[#141414] border border-white/15 rounded-md shadow-2xl overflow-hidden animate-fade-in">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <span className="text-sm font-semibold">Your Reminders</span>
                    <button onClick={() => setNotifOpen(false)} className="text-white/60 hover:text-white">
                      <X size={16} />
                    </button>
                  </div>
                  {remindedEvents.length === 0 ? (
                    <div className="px-4 py-6 text-center text-white/50 text-sm">
                      No reminders yet. Tap the bell on any upcoming game.
                    </div>
                  ) : (
                    <ul className="max-h-80 overflow-y-auto">
                      {remindedEvents.map((ev) => {
                        const isLive = liveIds.includes(ev.id);
                        return (
                          <li
                            key={ev.id}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 cursor-pointer"
                            onClick={() => { play(ev); setNotifOpen(false); }}
                          >
                            <img src={ev.thumb} alt="" className="w-14 h-9 object-cover rounded flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold line-clamp-1">{ev.title}</p>
                              <p className="text-[11px] text-white/50">
                                {isLive ? 'Live now' : ev.time}
                              </p>
                            </div>
                            {isLive ? <LiveBadge /> : <Bell size={14} className="text-[#e50914]" />}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/browse')}
              className="flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors"
            >
              <ChevronLeft size={18} /> Back to Netflix
            </button>
          </div>
        </div>
      </header>

      {/* Featured live */}
      <div className="relative w-full h-[60vw] max-h-[80vh] min-h-[420px]">
        <img src={featured.thumb} alt={featured.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-fade-left" />
        <div className="absolute inset-0 hero-fade-bottom" />
        <div className="absolute bottom-[16%] left-4 md:left-12 max-w-xl z-10">
          <div className="flex items-center gap-3 mb-3">
            <LiveBadge />
            <span className="text-xs md:text-sm text-white/80">{featured.league} · {featured.viewers}</span>
          </div>
          <h1 className="text-2xl md:text-5xl font-black drop-shadow-lg mb-2">{featured.title}</h1>
          <div className="flex items-center gap-4 mb-5 text-white/90">
            <span className="text-xl md:text-3xl font-bold">{featured.score}</span>
            <span className="bg-white/15 px-2 py-0.5 rounded text-sm">{featured.minute}</span>
          </div>
          <button
            onClick={() => play(featured)}
            className="flex items-center gap-2 bg-white text-black font-semibold px-6 md:px-8 py-2.5 rounded hover:bg-white/80 transition-colors"
          >
            <Play size={22} fill="black" /> Watch Live
          </button>
        </div>
      </div>

      <div className="relative z-20 -mt-12 md:-mt-20">
        {/* Live now */}
        <section className="mb-10">
          <h2 className="flex items-center gap-2 text-lg md:text-2xl font-bold px-4 md:px-12 mb-3">
            <Radio size={20} className="text-[#e50914]" /> Live Now
          </h2>
          <div className="flex gap-3 overflow-x-scroll no-scrollbar px-4 md:px-12">
            {liveNow.map((ev) => (
              <div
                key={ev.id}
                onClick={() => play(ev)}
                className="relative flex-shrink-0 w-64 md:w-80 cursor-pointer group"
              >
                <img src={ev.thumb} alt={ev.title} className="w-full h-36 md:h-44 object-cover rounded group-hover:brightness-75 transition-all" />
                <div className="absolute top-2 left-2"><LiveBadge /></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="bg-white text-black rounded-full p-3"><Play size={22} fill="black" /></span>
                </div>
                <div className="mt-2">
                  <p className="text-sm font-semibold line-clamp-1">{ev.title}</p>
                  <p className="text-xs text-white/60">{ev.sport} · {ev.score} · {ev.minute}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Upcoming */}
        <section className="mb-10">
          <h2 className="flex items-center gap-2 text-lg md:text-2xl font-bold px-4 md:px-12 mb-3">
            <Calendar size={20} className="text-white/80" /> Upcoming
          </h2>
          <div className="flex gap-3 overflow-x-scroll no-scrollbar px-4 md:px-12">
            {upcoming.map((ev) => {
              const isReminded = reminders.includes(ev.id);
              const isLive = liveIds.includes(ev.id);
              return (
                <div
                  key={ev.id}
                  onClick={() => play(ev)}
                  className="relative flex-shrink-0 w-60 md:w-72 cursor-pointer group"
                >
                  <img src={ev.thumb} alt={ev.title} className="w-full h-32 md:h-40 object-cover rounded group-hover:brightness-75 transition-all" />
                  <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/70 text-xs px-2 py-0.5 rounded">
                    {isLive ? <LiveBadge /> : (<><Clock size={12} /> {ev.time}</>)}
                  </div>
                  <div className="mt-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold line-clamp-1">{ev.title}</p>
                      <p className="text-xs text-white/60">{ev.sport}</p>
                    </div>
                    <button
                      onClick={(e) => toggleReminder(ev, e)}
                      className={`flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded border transition-colors ${
                        isReminded
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent text-white border-white/50 hover:border-white'
                      }`}
                    >
                      {isReminded ? <Check size={14} /> : <Bell size={14} />}
                      {isReminded ? 'Set' : 'Notify me'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Replays */}
        <section>
          <h2 className="text-lg md:text-2xl font-bold px-4 md:px-12 mb-3">Highlights & Replays</h2>
          <div className="flex gap-3 overflow-x-scroll no-scrollbar px-4 md:px-12">
            {replays.map((ev) => (
              <div
                key={ev.id}
                onClick={() => play(ev)}
                className="relative flex-shrink-0 w-56 md:w-72 cursor-pointer group"
              >
                <img src={ev.thumb} alt={ev.title} className="w-full h-32 md:h-40 object-cover rounded group-hover:brightness-75 transition-all" />
                <div className="absolute bottom-2 right-2 bg-black/70 text-xs px-2 py-0.5 rounded">{ev.duration}</div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="bg-white text-black rounded-full p-3"><Play size={22} fill="black" /></span>
                </div>
                <div className="mt-2">
                  <p className="text-sm font-semibold line-clamp-1">{ev.title}</p>
                  <p className="text-xs text-white/60">{ev.sport}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <VideoModal
        open={modal.open}
        youtubeKey={modal.key}
        title={modal.title}
        onClose={() => setModal({ open: false, key: null, title: '' })}
      />

      <Toaster position="top-center" theme="dark" />
    </div>
  );
}
